// Fichier : NewSiteVitrine_Minimalist_Chic.js

import React, { useRef } from "react";
import styled from "styled-components";
import {
  FaInstagram,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaMapMarkerAlt,
  FaStar,
} from "react-icons/fa";

// =======================================================================
// === 1. COLORS, BREAKPOINTS, ET DONNÉES ===
// =======================================================================

// === COLORS ===
const colors = {
  primary: "#1A1A1A", // Noir très foncé (Arrière-plan sombre, texte principal)
  secondary: "#FFFFFF", // Blanc pur (Arrière-plan clair, texte sur fond sombre)
  accent: "#B8860B", // Or (Touches d'accentuation)
  softText: "#888888", // Gris clair (Texte secondaire, descriptions)
  line: "#E0E0E0", // Ligne de séparation très claire
  placeholder: "transparent", // COULEUR DE PLACEHOLDER D'IMAGE
};

// === BREAKPOINTS ===
const MOBILE_BREAKPOINT = "600px";

const BOOKING_URL = "https://app.cal.eu/clindoeilinstitut";

// === DONNÉES DE TARIFS SIMPLIFIÉES ===
const ModernServiceData = [
  {
    title: "Soin Visage Hydraskin Avancé",
    duration: "1h30",
    price: "90 €",
    description: "Nettoyage en profondeur, hydratation et luminothérapie LED.",
  },
  {
    title: "Brow Lamination & Teinture",
    duration: "1h",
    price: "70 €",
    description:
      "Restructuration et fixation pour un sourcil parfait et fourni.",
  },
  {
    title: "Extensions Volume Russe Luxe",
    duration: "2h",
    price: "120 €",
    description: "Volume intense et effet cil à cil pour un regard glamour.",
  },
  {
    title: "Manucure Signature & Semi-Permanent",
    duration: "1h",
    price: "45 €",
    description: "Soin complet des mains, pose de vernis longue durée.",
  },
];

// =======================================================================
// === 2. STYLED COMPONENTS (CSS) ===
// =======================================================================

// === GLOBAL WRAPPER & TYPOGRAPHIE ===
const ModernWrapper = styled.div`
  width: 100vw;
  min-height: 100vh;
  background: ${colors.secondary};
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: hidden;
  font-family: "Montserrat", sans-serif;
`;

const ModernContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModernButton = styled.a.attrs(() => ({
  href: BOOKING_URL,
  target: "_blank",
  rel: "noopener noreferrer",
}))`
  padding: 12px 30px;
  background-color: ${colors.accent};
  color: ${colors.secondary};
  border: 2px solid ${colors.accent};
  border-radius: 4px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;

  &:hover {
    background-color: ${colors.primary};
    color: ${colors.accent};
    border-color: ${colors.accent};
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 8px 15px;
    font-size: 0.9rem;
  }
`;

// === HEADER & NAVIGATION ===
const ModernHeader = styled.header`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 40px;
  box-sizing: border-box;
  background: ${colors.secondary};
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid ${colors.line};

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 15px 20px;
  }
`;

const Logo = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${colors.primary};
  letter-spacing: 2px;
  cursor: pointer;
  span {
    color: ${colors.accent};
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 1.5rem;
  }
`;

const ModernNav = styled.nav`
  display: flex;
  gap: 35px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;

const ModernNavItem = styled.p`
  color: ${colors.primary};
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  position: relative;
  transition: color 0.3s;

  &::after {
    content: "";
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -5px;
    left: 0;
    background: ${colors.accent};
    transition: width 0.3s;
  }

  &:hover {
    color: ${colors.accent};
    &::after {
      width: 100%;
    }
  }
`;

// === SECTION : HERO ===
const HeroSection = styled.div`
  width: 100%;
  height: 80vh;
  background: ${colors.primary};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: -1px;
  position: relative;
  overflow: hidden;
`;

const HeroImagePlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${colors.placeholder}; /* Fond rouge */
  opacity: 0.7;
  /* Remplacé par une URL pour simuler l'image pour le style précédent */
  /* backgroundImage: url(https://via.placeholder.com/1200x800/222222/FFFFFF?text=Modern+Aesthetic+Studio); */
`;

const HeroTextContainer = styled.div`
  z-index: 10;
  color: ${colors.secondary};
  max-width: 800px;
  padding: 20px;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 300;
  letter-spacing: 5px;
  text-transform: uppercase;
  margin-bottom: 20px;

  strong {
    font-weight: 700;
    color: ${colors.accent};
  }

  @media (max-width: 900px) {
    font-size: 3rem;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 2rem;
    letter-spacing: 2px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 40px;
  color: ${colors.softText};
`;

// === SECTION GÉNÉRIQUE ET TITRE ===
const ModernSection = styled.section`
  width: 100%;
  padding: 100px 40px;
  box-sizing: border-box;
  text-align: center;
  background: ${({ bg }) => bg || colors.secondary};
  color: ${({ bg }) =>
    bg === colors.primary ? colors.secondary : colors.primary};

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 60px 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${colors.primary};

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 2rem;
  }
`;

// === SERVICES & TARIFS ===
const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 50px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
`;

const ServiceCard = styled.div`
  background: ${colors.secondary};
  border: 1px solid ${colors.line};
  border-radius: 8px;
  padding: 30px;
  text-align: left;
  transition: box-shadow 0.3s, transform 0.3s;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  height: 100%;
  box-sizing: border-box;

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    transform: translateY(-5px);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 10px;
  color: ${colors.primary};
`;

const CardDetail = styled.p`
  font-size: 1rem;
  color: ${colors.softText};
  margin-bottom: 5px;
  line-height: 1.5;
`;

const CardPrice = styled.p`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${colors.accent};
  margin-top: 15px;
`;

// === TÉMOIGNAGES ===
const ReviewsGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
  color: white;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: center;
    gap: 30px;
  }
`;

const ReviewCard = styled.div`
  flex: 1;
  max-width: 450px;
  font-style: italic;
  line-height: 1.7;
  text-align: left;

  cite {
    display: block;
    margin-top: 15px;
    font-style: normal;
    font-weight: bold;
    color: ${colors.softText};
  }
`;

// === CONTACT ===
const ContactGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 60px;
  margin-top: 40px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-direction: column;
    gap: 20px;
    align-items: center;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  text-align: left;

  svg {
    color: ${colors.accent};
    font-size: 1.5rem;
    flex-shrink: 0;
  }

  p,
  a {
    font-size: 1rem;
    color: ${colors.primary};
    font-weight: 500;
    text-decoration: none;
  }

  a:hover {
    color: ${colors.accent};
  }
`;

// === GOOGLE MAP SECTION ===
const MapSection = styled.section`
  width: 100%;
  padding: 0;
  display: flex;
  justify-content: center;
  background: ${colors.secondary};
`;

const MapInner = styled.div`
  width: 100%;
  max-width: 1200px;
  padding: 0 40px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 20px;
  }
`;

const MapContainer = styled.div`
  width: 100%;
  height: 450px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  margin: 80px 0; /* Espacement important avant le footer */

  @media (max-width: 900px) {
    height: 350px;
  }
`;

const MapInfoLine = styled.p`
  font-size: 1rem;
  color: ${colors.primary};
  margin-top: 10px;
`;

// === FOOTER ===
const ModernFooter = styled.footer`
  width: 100%;
  background: ${colors.primary};
  color: ${colors.softText};
  padding: 40px 40px 20px;
  text-align: center;
`;

const FooterNav = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-bottom: 20px;

  a {
    color: ${colors.softText};
    text-decoration: none;
    transition: color 0.3s;
    font-size: 0.95rem;
    &:hover {
      color: ${colors.accent};
    }
  }
`;

// === PLACEHOLDER DE GALERIE (NOUVEAU) ===
const GalleryPlaceholder = styled.div`
  width: 100%;
  height: 350px;
  background: ${colors.placeholder};
  margin: 50px 0;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${colors.secondary};
  font-size: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 250px;
    font-size: 1rem;
  }
`;

// =======================================================================
// === 3. COMPOSANT PRINCIPAL (FONCTION) ===
// =======================================================================

const NewSiteVitrine = () => {
  // Références pour la navigation
  const homeRef = useRef(null);
  const servicesRef = useRef(null);
  const tarifsRef = useRef(null);
  const contactRef = useRef(null);

  const handleNavigation = (targetRef) => {
    targetRef?.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <ModernWrapper>
      {/* HEADER */}
      <ModernHeader>
        <Logo onClick={() => handleNavigation(homeRef)}>
          LE STUDI<span>O</span> LUXE
        </Logo>
        <ModernNav>
          <ModernNavItem onClick={() => handleNavigation(homeRef)}>
            Accueil
          </ModernNavItem>
          <ModernNavItem onClick={() => handleNavigation(servicesRef)}>
            Services
          </ModernNavItem>
          <ModernNavItem onClick={() => handleNavigation(tarifsRef)}>
            Tarifs
          </ModernNavItem>
          <ModernNavItem onClick={() => handleNavigation(contactRef)}>
            Contact
          </ModernNavItem>
        </ModernNav>
        <ModernButton>Réserver</ModernButton>
      </ModernHeader>

      {/* SECTION 1 : HERO */}
      <HeroSection ref={homeRef}>
        <HeroImagePlaceholder>IMAGE HERO (Fond Rouge)</HeroImagePlaceholder>
        <HeroTextContainer>
          <HeroTitle>
            L'art de l'esthétique <strong>minimaliste</strong>
          </HeroTitle>
          <HeroSubtitle>
            Votre adresse exclusive pour des soins du regard et du visage
            avant-gardistes.
          </HeroSubtitle>
          <ModernButton
            style={{
              backgroundColor: colors.accent,
              borderColor: colors.accent,
            }}
          >
            Réserver maintenant
          </ModernButton>
          {/* Bouton gardé pour l'action principale "Réserver" */}
        </HeroTextContainer>
      </HeroSection>

      {/* PLACEHOLDER DE GALERIE/BLOC IMAGE APRÈS HERO */}
      <ModernContainer>
        <GalleryPlaceholder>BLOC IMAGE / GALERIE</GalleryPlaceholder>
      </ModernContainer>

      {/* SECTION 2 : SERVICES & TARIFS */}
      <ModernSection ref={servicesRef}>
        <SectionTitle style={{ color: colors.primary }}>
          Nos Services Phares
        </SectionTitle>
        <p
          style={{
            maxWidth: "700px",
            margin: "0 auto 50px",
            color: colors.softText,
          }}
        >
          Nous utilisons des techniques de pointe et des produits haut de gamme
          pour garantir des résultats d'exception.
        </p>
        <ServiceGrid ref={tarifsRef}>
          {ModernServiceData.map((service, index) => (
            <ServiceCard key={index}>
              <CardTitle>{service.title}</CardTitle>
              <CardDetail style={{ marginBottom: "15px" }}>
                {service.description}
              </CardDetail>
              <CardDetail>Durée : {service.duration}</CardDetail>
              <CardPrice>{service.price}</CardPrice>
            </ServiceCard>
          ))}
        </ServiceGrid>
      </ModernSection>

      <hr
        style={{
          width: "80%",
          borderTop: `1px solid ${colors.line}`,
          margin: "0 auto",
        }}
      />

      {/* SECTION 3 : TÉMOIGNAGES (BLOC SUR FOND SOMBRE) */}
      <ModernSection bg={colors.primary}>
        <SectionTitle style={{ color: colors.accent, marginBottom: "50px" }}>
          Ce que disent nos clients
        </SectionTitle>
        <ReviewsGrid style={{ color: colors.secondary }}>
          <ReviewCard
            style={{
              borderLeft: `3px solid ${colors.accent}`,
              paddingLeft: "20px",
            }}
          >
            <FaStar style={{ color: colors.accent, marginBottom: "10px" }} />
            <p>
              "Le studio est magnifique. Le soin Hydraskin était spectaculaire,
              ma peau n'a jamais été aussi lumineuse."
            </p>
            <cite>Chloé D.</cite>
          </ReviewCard>
          <ReviewCard
            style={{
              borderLeft: `3px solid ${colors.accent}`,
              paddingLeft: "20px",
            }}
          >
            <FaStar style={{ color: colors.accent, marginBottom: "10px" }} />
            <p>
              "Professionnalisme et ambiance zen. La pose d'extensions est la
              plus naturelle que j'ai jamais eue. Je recommande vivement."
            </p>
            <cite>Léa M.</cite>
          </ReviewCard>
        </ReviewsGrid>
        {/* Suppression du bouton "Découvrir les avis" */}
      </ModernSection>

      {/* PLACEHOLDER DE BLOC IMAGE AVANT LE CONTACT */}
      <ModernContainer>
        <GalleryPlaceholder
          style={{ backgroundColor: colors.primary, color: colors.secondary }}
        >
          IMAGE PROMOTIONNELLE / PRODUIT
        </GalleryPlaceholder>
      </ModernContainer>

      {/* SECTION 4 : CONTACT / HORAIRES */}
      <ModernSection ref={contactRef} style={{ paddingBottom: "0" }}>
        <SectionTitle style={{ color: colors.primary, marginBottom: "40px" }}>
          Contact & Réservation
        </SectionTitle>
        <ContactGrid>
          {/* Horaires */}
          <ContactItem>
            <FaClock />
            <div>
              <p style={{ fontWeight: "700", color: colors.primary }}>
                Horaires
              </p>
              <CardDetail>Lun - Ven : 10h00 - 19h00</CardDetail>
              <CardDetail>Sam : 10h00 - 15h00</CardDetail>
            </div>
          </ContactItem>
          {/* Téléphone cliquable */}
          <ContactItem>
            <FaPhone />
            <div>
              <p style={{ fontWeight: "700", color: colors.primary }}>
                Téléphone
              </p>
              <a href="tel:0609405177">
                <CardDetail style={{ margin: 0 }}>06.09.40.51.77</CardDetail>
              </a>
            </div>
          </ContactItem>
          {/* E-mail cliquable */}
          <ContactItem>
            <FaEnvelope />
            <div>
              <p style={{ fontWeight: "700", color: colors.primary }}>E-mail</p>
              <a href="mailto:contact@studio-luxe.com">
                <CardDetail style={{ margin: 0 }}>
                  contact@studio-luxe.com
                </CardDetail>
              </a>
            </div>
          </ContactItem>
        </ContactGrid>
        <ModernButton style={{ marginTop: "50px" }}>
          Prendre rendez-vous
        </ModernButton>
      </ModernSection>

      <MapSection ref={contactRef}>
        <MapInner>
          <MapContainer>
            <iframe
              // *** Ceci est le SRC que vous devez copier entièrement depuis Google Maps ***
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5568.736270330388!2d3.162998176895719!3d45.74376521474145!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47f71d74fe8877c3%3A0x89284485db0bdfb6!2sZone%20industrielle%2C%201%20Rue%20des%20Manzats%2C%2063800%20Cournon-d&#39;Auvergne!5e0!3m2!1sfr!2sfr!4v1765275400880!5m2!1sfr!2sfr"
            ></iframe>
          </MapContainer>
        </MapInner>
      </MapSection>

      {/* FOOTER */}
      <ModernFooter>
        <Logo style={{ color: colors.secondary, marginBottom: "15px" }}>
          LE STUDI<span>O</span> LUXE
        </Logo>
        <FooterNav>
          <a href="#">Mentions Légales</a>
          <a href="#">Politique de Confidentialité</a>
          <a
            href="https://www.instagram.com/fannyjunger"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaInstagram
              style={{ verticalAlign: "middle", marginRight: "5px" }}
            />{" "}
            Instagram
          </a>
        </FooterNav>
        <p style={{ fontSize: "0.8rem", marginTop: "20px" }}>
          © 2025 Le Studio Luxe. Tous droits réservés.
        </p>
      </ModernFooter>
    </ModernWrapper>
  );
};

export default NewSiteVitrine;
